import {chainWalk} from "../../../util/Util";
import {formatAisleLabel, getDisplayableColumnLabel, processFlexColumns, createColumnLabelMapping} from "../../../util/LidarTesterUtil";
import {LIDAR_TESTER_SENSOR_STATES, SENSOR_INDEX_ONE, SENSOR_INDEX_ZERO} from "../../../util/Constants";
import {WW_FLEX_CART_RECIPES} from "../../../util/Validations";

/**
 * Validates if a LidarTester message has all required fields
 * @param {Object} message - The LidarTester message
 * @returns {boolean} - Whether the message is valid
 */
const isValidLidarTesterMessage = (message) => {
    return message && 
           message.aisleLabel && 
           message.columnLabel && 
           message.address !== undefined && 
           message.sensorIndex !== undefined;
};

/**
 * LidarTesterReducer - Manages sensor state mapping for Lidar testing system
 * 
 * SENSOR STATE MAP STRUCTURE:
 * sensorStateMap = {
 *   [aisleLabel]: {           // e.g., "A-1", "B2"
 *     [columnLabel]: {        // e.g., "A", "B", "AB", "TU", "WX"
 *       [columnAddress]: {    // e.g., "1.1", "1.2", "1.3"
 *         [sensorIndex]: {    // "0" or "1" (SENSOR_INDEX_ZERO/ONE)
 *           state: number,    // LIDAR_TESTER_SENSOR_STATES enum value
 *           time: number,     // timestamp
 *           value: string     // sensor reading value
 *         }
 *       }
 *     }
 *   }
 * }
 * 
 */
export const LidarTesterReducer = (state, action) => {    
    switch (action.type) {
        case "LOAD_STATE_MAP":
            // Check if we already have data for this controller to avoid unnecessary updates
            if (state.controllerMacAddress === action.data.macAddress && 
                state.sensorStateMap && Object.keys(state.sensorStateMap).length > 0) {
                return state;
            }            

            return {
                ...state, 
                sensorStateMap: createMap(action.data.sortedAssetByAisle, action.data.isSensorLevelConfigEnabled, action.data.cartRecipesMap),
                controllerMacAddress: action.data.macAddress, 
                columnsList: action.data.columnsList
            }
        /**
         * Updates individual sensor states from incoming MQTT messages
         * 
         * COLUMN MATCHING LOGIC:
         * 1. Exact match: message.columnLabel === state column
         * 2. Component match: state column contains message.columnLabel (for unified columns)
         * 
         * SENSOR VALUE CALCULATION:
         * - OV bins (tier === -1): Use columnLabel directly
         * - Multi-column displays: Include tier + columnLabel for differentiation
         * - Single columns: Use tier value only
         */
        case "UPDATE_STATE_MAP":
            try {
                // Check if this is a provisioning message (has nodeType field)
                if (action.data.message.nodeType) {
                    return state;
                }
                
                // Validate required fields
                if (!isValidLidarTesterMessage(action.data.message)) {
                    return state;
                }
                
                let aisleLabel = formatAisleLabel(action.data.message.aisleLabel);
                
                // Find the column that matches the incoming message (no slash notation)
                let matchingColumn = null;
                
                for (const col of state.columnsList) {
                    // Check if column exists in state map and is not anomalous
                    if (!isAnomaly(state.sensorStateMap, aisleLabel, col)) {
                        // For unified columns, check if message column is a component
                        if (col.length > 1 && col.includes(action.data.message.columnLabel)) {
                            matchingColumn = col;
                            break;
                        }
                        // For exact matches
                        if (col === action.data.message.columnLabel) {
                            matchingColumn = col;
                            break;
                        }
                    }
                }
                
                // If no match found in columnsList, check sensor state map directly for combined columns
                // This handles cases where flex columns are marked as 'mixed' and split in UI but still exist as combined in sensor map
                if (!matchingColumn && state.sensorStateMap[aisleLabel]) {
                    const availableColumns = Object.keys(state.sensorStateMap[aisleLabel]);
                    for (const col of availableColumns) {
                        // Check for combined columns that contain the message column as a component
                        if (col.length > 1 && col.includes(action.data.message.columnLabel)) {
                            matchingColumn = col;
                            break;
                        }
                        // Check for exact matches
                        if (col === action.data.message.columnLabel) {
                            matchingColumn = col;
                            break;
                        }
                    }
                }
                        
                if (!matchingColumn) {
                    console.log("No matching column found for LidarTester message, ignoring");
                    return state;
                }
                
                if (chainWalk(() => getSensorState(state.sensorStateMap, action.data.message, matchingColumn, aisleLabel), false)) {
                    state.sensorStateMap[aisleLabel][matchingColumn][action.data.message.address][action.data.message.sensorIndex.toString()] = {
                        ...getUpdatedSensorState(action.data.message.timestamp,
                            action.data.message.tier,
                            action.data.message.columnLabel,
                            matchingColumn)
                    };
                }
                return {...state}
            } catch (error) {
                console.error("Error processing LidarTester message:", error);
                return state;
            }
        default:
            return state
    }
}

const containsMultipleColumn = (displayColumnLabel) => {
    return displayColumnLabel && displayColumnLabel.length > 1;
};

const getSensorValue = (tier, columnLabel, displayColumnLabel) => {
    //Tier value is -1 in case of OV bin label.
    if (tier === -1) return columnLabel;
    //If displayColumnLabel is AB then sensor value should have column Label to differentiate between A or B column event.
    //It is currently required for Flex rack.
    return containsMultipleColumn(displayColumnLabel) ? tier + columnLabel : tier;
};

const getUpdatedSensorState = (timestamp, tier, columnLabel, displayColumnLabel) => {
    return {
        state: LIDAR_TESTER_SENSOR_STATES.STOW_DETECTED,
        time: timestamp,
        value: getSensorValue(tier, columnLabel, displayColumnLabel)
    }
}

const getSensorState = (sensorStateMap, message, columnLabel, aisleLabel) => {
    return sensorStateMap[aisleLabel][columnLabel][message.address][message.sensorIndex.toString()];
}

export const initialSensorState = {
    state: LIDAR_TESTER_SENSOR_STATES.DEFAULT,
    time: Date.now(),
    value: ""
}

const isAnomaly = (sensorStateMap, aisleLabel, label) => {
    return chainWalk(() => !sensorStateMap[aisleLabel][label], false)
}

/**
 * Example sensor state initialization:
 * initializeSensorStates(map, "A-1", "AB", "1.1", false)
 * Creates: map["A-1"]["AB"]["1.1"]["0"] = initialSensorState
 *         map["A-1"]["AB"]["1.1"]["1"] = initialSensorState
 */
const initializeSensorStates = (map, aisleLabel, columnLabel, columnAddress, isSensorLevelConfigEnabled) => {
    if (!map[aisleLabel][columnLabel]) {
        map[aisleLabel][columnLabel] = {};
    }
    map[aisleLabel][columnLabel][columnAddress] = {};
    map[aisleLabel][columnLabel][columnAddress][SENSOR_INDEX_ZERO] = initialSensorState;
    if (!isSensorLevelConfigEnabled) {
        map[aisleLabel][columnLabel][columnAddress][SENSOR_INDEX_ONE] = initialSensorState;
    }
};

/**
 * Creates the complete sensor state map from asset configuration
 * 
 * PROCESSING FLOW:
 * 1. Initialize empty aisle structures
 * 2. Separate flex vs non-flex columns per aisle
 * 3. Process flex columns with sensor sharing logic
 * 4. Process non-flex columns through label unification
 * 
 * FLEX COLUMN LOGIC:
 * - For 2-char combined labels (e.g., "JK"): Creates separate J,K columns with shared middle sensor
 * - For unified labels: Creates single combined column for all sensors
 * 
 * @param {Array} sortedAsset - Asset mappings by aisle
 * @param {boolean} isSensorLevelConfigEnabled - Controls sensor index creation
 * @param {Map} cartRecipesMap - Cart recipe configurations for flex processing
 * @returns {Object} Complete sensor state map structure
 */
const createMap = (sortedAsset, isSensorLevelConfigEnabled, cartRecipesMap) => {
    let map = {};
    if (!sortedAsset || sortedAsset.length === 0) return map;
    
    // Initialize aisle maps
    sortedAsset.forEach(assetMapping => {
        const aisleLabel = formatAisleLabel(assetMapping.aisleLabel);
        map[aisleLabel] = {};
    });
    
    // Process each aisle individually to handle mixed configurations
    sortedAsset.forEach(assetMapping => {
        const aisleLabel = formatAisleLabel(assetMapping.aisleLabel);
        if (!assetMapping.columns) return;
        
        // Separate flex and non-flex columns for this aisle
        const flexColumns = [], nonFlexColumns = [];
        assetMapping.columns.forEach(column => {
            if (WW_FLEX_CART_RECIPES.includes(column.cartType)) {
                flexColumns.push(column);
            } else {
                nonFlexColumns.push(column);
            }
        });
        
        // Process flex columns for this aisle with shared middle sensor logic
        if (flexColumns.length > 0) {
            processFlexColumns(flexColumns, cartRecipesMap).forEach(({ combinedLabel, group }) => {
                // SENSOR SHARING FOR MIXED FLEX CONFIGURATIONS:
                // When combinedLabel.length === 2 (e.g., "JK"):
                // - Sensor 0: Left column only (J)
                // - Sensor 1: Both columns (J and K) - SHARED
                // - Sensor 2: Right column only (K)
                // This allows one physical sensor to serve multiple logical columns
                if (combinedLabel.length === 2) { // e.g., "JK"
                    const leftLabel = combinedLabel[0];   // "J"
                    const rightLabel = combinedLabel[1];  // "K"
                    
                    group.forEach((column, index) => {
                        if (index === 0) {
                            // First sensor: Left column only
                            initializeSensorStates(map, aisleLabel, leftLabel, column.columnAddress, isSensorLevelConfigEnabled);
                        } else if (index === 1) {
                            // Middle sensor: Both columns (shared)
                            initializeSensorStates(map, aisleLabel, leftLabel, column.columnAddress, isSensorLevelConfigEnabled);
                            initializeSensorStates(map, aisleLabel, rightLabel, column.columnAddress, isSensorLevelConfigEnabled);
                        } else if (index === 2) {
                            // Last sensor: Right column only
                            initializeSensorStates(map, aisleLabel, rightLabel, column.columnAddress, isSensorLevelConfigEnabled);
                        }
                    });
                } else {
                    // Unified column (all aisles have flex) - use combined label
                    group.forEach(column => {
                        initializeSensorStates(map, aisleLabel, combinedLabel, column.columnAddress, isSensorLevelConfigEnabled);
                    });
                }
            });
        }
        
        // Process non-flex columns through unification
        if (nonFlexColumns.length > 0) {
            const device = { displayableAssetMappings: [{ columns: nonFlexColumns }] };
            const { labelMapping } = createColumnLabelMapping(device);
            
            nonFlexColumns.forEach(column => {
                try {
                    const originalLabel = getDisplayableColumnLabel(column.columnLabel, column.rowLabels, column.cartType);
                    let targetLabel = labelMapping[originalLabel] || originalLabel;
                    
                    // DYNAMIC LABEL UNIFICATION:
                    // Check if this single label should be unified with adjacent labels
                    // Example: If labelMapping contains "TU" -> ["T", "U"], then single "T" or "U" 
                    // columns should use unified "TU" label for consistency across aisles
                    const unifiedLabel = Object.keys(labelMapping).find(key => 
                        labelMapping[key] && labelMapping[key].includes(originalLabel) && labelMapping[key] !== originalLabel
                    );
                    
                    if (unifiedLabel && labelMapping[originalLabel] !== unifiedLabel) {
                        // Use the unified label when other aisles have the unified version
                        targetLabel = unifiedLabel;
                    }
                    
                    initializeSensorStates(map, aisleLabel, targetLabel, column.columnAddress, isSensorLevelConfigEnabled);
                } catch (error) {
                    console.error("Error processing non-flex column:", error);
                }
            });
        }
    });
    
    return map;
}



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/reducer/LidarTesterReducer.js